// F6 — Coach Edit Enhancements.
//
// Full-field Asset form organized into tabs:
//   1. Identity
//   2. Operations
//   3. Capacity & Features
//   4. Rates & Pricing
//   5. Descriptions & Notes
//   6. Advanced (photo + legacy)
//
// Required minimum on CREATE: `Name / Unit Number` (unit_name) + `Type`.
// All other fields are optional. On EDIT we send back the full nested
// `config` and `rate_defaults` objects because the backend PATCH uses
// $set which replaces nested subdocuments.
//
// F6 defers Odometer / Generator Hours / Insurance / Title schemas per
// owner authorization. No schema was added.

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { assetApi, unitTypeApi, orgApi, assetOwnerApi, driverApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImagePlus, X as XIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// RC10f · Correction B. Unit types are now sourced live from
// `/api/unit-types` (see <UnitTypeSelect/> below). The historical
// `entertainer_coach` / `band_crew_coach` codes are preserved on
// existing records: if the asset's current `asset_type` isn't in
// the active list, we render it with an `[Archived]` prefix so
// readers still see a friendly label.
const OWNERSHIP_TYPES = ["owned", "leased", "managed", "other"];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

// Default config shape — must match backend AssetConfig defaults exactly.
const DEFAULT_CONFIG = {
  bunk_count: "",
  slide_count: "",
  tv_count: "",
  fresh_water_gal: "",
  gray_water_gal: "",
  black_water_gal: "",
  star_room: false,
  front_lounge: false,
  rear_lounge: false,
  restroom: false,
  shower: false,
  washer_dryer: false,
  refrigerator: false,
  freezer: false,
  microwave: false,
  stovetop: false,
  satellite: false,
  starlink: false,
  wifi: false,
  generator: false,
  shore_power: false,
  amenities: [],
  custom_notes: "",
};

const DEFAULT_RATES = {
  daily_rate: "",
  weekly_rate: "",
  monthly_rate: "",
  mileage_rate: "",
  lease_amount: "",
  commission_percent: "",
  commission_flat: "",
  satellite_amount: "",
  internet_amount: "",
  cleaning_amount: "",
  generator_amount: "",
  other_amount: "",
};

const mergeConfig = (initial) => ({
  ...DEFAULT_CONFIG,
  ...(initial?.config || {}),
  bunk_count: initial?.config?.bunk_count ?? "",
  slide_count: initial?.config?.slide_count ?? "",
  tv_count: initial?.config?.tv_count ?? "",
  fresh_water_gal: initial?.config?.fresh_water_gal ?? "",
  gray_water_gal: initial?.config?.gray_water_gal ?? "",
  black_water_gal: initial?.config?.black_water_gal ?? "",
  amenities: initial?.config?.amenities || [],
  custom_notes: initial?.config?.custom_notes || "",
});

const mergeRates = (initial) => {
  const out = { ...DEFAULT_RATES };
  for (const k of Object.keys(DEFAULT_RATES)) {
    const v = initial?.rate_defaults?.[k];
    out[k] = v == null ? "" : String(v);
  }
  return out;
};

// Coerce optional integer text ("" -> null, "10" -> 10). Rejects NaN.
const toIntOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

// Coerce optional decimal text ("" -> null, "1234.56" -> "1234.56"). Backend
// accepts strings and coerces to Decimal via Pydantic. Reject obvious junk.
const toDecOrNull = (v) => {
  if (v === "" || v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (!/^-?\d*(\.\d+)?$/.test(s)) return null;
  return s;
};

export const AssetFormDialog = ({ open, orgs, onClose, onSaved, initial }) => {
  const isEdit = !!initial?.id;
  const isTrailer = (initial?.asset_type || "twelve_bunk") === "trailer";

  const [form, setForm] = useState({
    unit_name: initial?.unit_name || "",
    fleet_number: initial?.fleet_number || "",
    asset_type: initial?.asset_type || "twelve_bunk",
    year: initial?.year || "",
    make: initial?.make || "",
    model: initial?.model || "",
    chassis: initial?.chassis || "",
    converter: initial?.converter || "",
    vin: initial?.vin || "",
    license_plate: initial?.license_plate || "",
    // Phase 2 (2026-09-10) additive fields.
    door_code: initial?.door_code || "",
    engine_serial: initial?.engine_serial || "",
    engine_make_model: initial?.engine_make_model || "",
    registration_state: initial?.registration_state || "",
    registration_expiration: initial?.registration_expiration || "",
    owner_organization_id: initial?.owner_organization_id || "",
    ownership_type: initial?.ownership_type || "",
    // RC10j-10-14 · Primary Assigned Driver — home-unit assignment,
    // separate from Tour/Schedule/Dispatch driver fields.
    primary_driver_id: initial?.primary_driver_id || "",
    active: initial?.active ?? true,
    notes: initial?.notes || "",
    quote_description: initial?.quote_description || "",
    schedule_description: initial?.schedule_description || "",
    primary_photo_file_id: initial?.primary_photo_file_id || null,
    config: mergeConfig(initial),
    rate_defaults: mergeRates(initial),
  });

  const [tab, setTab] = useState("identity");
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // RC10h · Archive handler wired to the sticky-footer Archive button.
  // Edit-mode only; preserves the existing `assetApi.archive` endpoint
  // + confirmation flow (window.confirm so no new modal state).
  const handleArchive = async () => {
    if (!initial?.id) return;
    if (!window.confirm("Archive this asset? It can be restored by a Super Admin.")) return;
    setArchiving(true);
    try {
      await assetApi.archive(initial.id);
      toast.success("Asset archived");
      onSaved?.(); // let caller refresh list / detail
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Archive failed");
    } finally {
      setArchiving(false);
    }
  };
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  // RC10f · Correction B — live Unit Type catalog
  const [unitTypes, setUnitTypes] = useState([]);
  useEffect(() => {
    unitTypeApi.list().then((rows) => setUnitTypes(rows || [])).catch(() => {});
  }, []);
  // RC10f · Correction C — searchable Owner Organization dropdown
  const [ownerQuery, setOwnerQuery] = useState("");
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerPanelOpen, setOwnerPanelOpen] = useState(false);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [currentOwnerName, setCurrentOwnerName] = useState("");
  const [currentOwnerArchived, setCurrentOwnerArchived] = useState(false);
  const [currentOwnerMissing, setCurrentOwnerMissing] = useState(false);
  // When form.owner_organization_id changes OR mounts with a value,
  // fetch the owner's display name + archived flag so we can render
  // "[Archived] <name>" or "Missing organization".
  useEffect(() => {
    let cancelled = false;
    const id = form.owner_organization_id;
    if (!id) { setCurrentOwnerName(""); setCurrentOwnerArchived(false); setCurrentOwnerMissing(false); return; }
    orgApi.get(id).then((o) => {
      if (cancelled) return;
      setCurrentOwnerName(o?.display_name || id);
      setCurrentOwnerArchived(!!o?.is_archived);
      setCurrentOwnerMissing(false);
    }).catch(() => {
      if (cancelled) return;
      setCurrentOwnerName("");
      setCurrentOwnerArchived(false);
      setCurrentOwnerMissing(true);
    });
    return () => { cancelled = true; };
  }, [form.owner_organization_id]);
  // Debounced search on orgs — only active asset-owner orgs are offered for assignment.
  // RC10h · scope the picker to Asset Owners (coach_owner/trailer_owner).
  useEffect(() => {
    const h = setTimeout(() => {
      setOwnerLoading(true);
      assetOwnerApi.list({ q: ownerQuery || undefined, page_size: 25 })
        .then((d) => setOwnerOptions(d?.results || []))
        .catch(() => setOwnerOptions([]))
        .finally(() => setOwnerLoading(false));
    }, 180);
    return () => clearTimeout(h);
  }, [ownerQuery]);

  // RC10h · Compact summary (company + billing city/state + owner-person + phone).
  // Only populated when the current owner is a Asset Owner (coach_owner / trailer_owner).
  // For legacy non-asset-owner orgs the summary is simply omitted.
  const [ownerSummary, setOwnerSummary] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const id = form.owner_organization_id;
    setOwnerSummary(null);
    if (!id) return;
    assetOwnerApi.get(id)
      .then((d) => { if (!cancelled) setOwnerSummary(d); })
      .catch(() => { if (!cancelled) setOwnerSummary(null); });
    return () => { cancelled = true; };
  }, [form.owner_organization_id]);

  // RC10h · Quick-add — opens a mini "create asset owner" dialog in-place.
  // On success the dropdown is refetched and the newly created owner is auto-selected.
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  // RC10j-10-14 · Primary Assigned Driver searchable dropdown state.
  const [driverQuery, setDriverQuery] = useState("");
  const [driverOptions, setDriverOptions] = useState([]);
  const [driverPanelOpen, setDriverPanelOpen] = useState(false);
  const [driverLoading, setDriverLoading] = useState(false);
  const [currentDriverLabel, setCurrentDriverLabel] = useState("");
  const [currentDriverArchived, setCurrentDriverArchived] = useState(false);
  const [currentDriverMissing, setCurrentDriverMissing] = useState(false);
  const [transferConfirm, setTransferConfirm] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const id = form.primary_driver_id;
    if (!id) {
      setCurrentDriverLabel("");
      setCurrentDriverArchived(false);
      setCurrentDriverMissing(false);
      return;
    }
    driverApi.get(id).then((d) => {
      if (cancelled) return;
      setCurrentDriverLabel(d?.display_name || id);
      setCurrentDriverArchived(!!d?.is_archived);
      setCurrentDriverMissing(false);
    }).catch(() => {
      if (cancelled) return;
      setCurrentDriverLabel("");
      setCurrentDriverArchived(false);
      setCurrentDriverMissing(true);
    });
    return () => { cancelled = true; };
  }, [form.primary_driver_id]);
  useEffect(() => {
    const h = setTimeout(() => {
      setDriverLoading(true);
      driverApi.list({ q: driverQuery || undefined, active: true, page_size: 25 })
        .then((d) => setDriverOptions(d?.results || []))
        .catch(() => setDriverOptions([]))
        .finally(() => setDriverLoading(false));
    }, 180);
    return () => clearTimeout(h);
  }, [driverQuery]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setConfig = (k, v) => setForm((f) => ({ ...f, config: { ...f.config, [k]: v } }));
  const setRate = (k, v) => setForm((f) => ({ ...f, rate_defaults: { ...f.rate_defaults, [k]: v } }));

  const isTypeTrailer = form.asset_type === "trailer";

  useEffect(() => {
    let cancelled = false;
    const loadPhoto = async () => {
      if (!form.primary_photo_file_id) { setPhotoPreviewUrl(null); return; }
      try {
        const token = localStorage.getItem("aa_ops_token");
        const r = await axios.get(
          `${BACKEND}/api/files/${form.primary_photo_file_id}/download-token`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) {
          setPhotoPreviewUrl(`${BACKEND}/api/files/${form.primary_photo_file_id}?token=${r.data.token}`);
        }
      } catch { if (!cancelled) setPhotoPreviewUrl(null); }
    };
    loadPhoto();
    return () => { cancelled = true; };
  }, [form.primary_photo_file_id]);

  const onPhotoPicked = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("aa_ops_token");
      const r = await axios.post(`${BACKEND}/api/files/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setField("primary_photo_file_id", r.data.id);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error("Photo upload failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const buildPayload = () => {
    const p = {
      unit_name: form.unit_name.trim(),
      fleet_number: form.fleet_number.trim() || null,
      asset_type: form.asset_type,
      year: form.year === "" ? null : Number(form.year),
      make: form.make.trim() || null,
      model: form.model.trim() || null,
      chassis: form.chassis.trim() || null,
      converter: form.converter.trim() || null,
      vin: form.vin.trim() || null,
      license_plate: form.license_plate.trim() || null,
      door_code: form.door_code.trim() || null,
      engine_serial: form.engine_serial.trim() || null,
      engine_make_model: form.engine_make_model.trim() || null,
      registration_state: form.registration_state || null,
      registration_expiration: form.registration_expiration || null,
      owner_organization_id: form.owner_organization_id || null,
      ownership_type: form.ownership_type || null,
      // RC10j-10-14 — primary_driver_id serialized to null when blank.
      primary_driver_id: form.primary_driver_id || null,
      active: !!form.active,
      notes: form.notes.trim() || null,
      quote_description: form.quote_description.trim() || null,
      schedule_description: form.schedule_description.trim() || null,
      primary_photo_file_id: form.primary_photo_file_id || null,
      config: {
        bunk_count: toIntOrNull(form.config.bunk_count),
        slide_count: toIntOrNull(form.config.slide_count),
        tv_count: toIntOrNull(form.config.tv_count),
        fresh_water_gal: toIntOrNull(form.config.fresh_water_gal),
        gray_water_gal: toIntOrNull(form.config.gray_water_gal),
        black_water_gal: toIntOrNull(form.config.black_water_gal),
        star_room: !!form.config.star_room,
        front_lounge: !!form.config.front_lounge,
        rear_lounge: !!form.config.rear_lounge,
        restroom: !!form.config.restroom,
        shower: !!form.config.shower,
        washer_dryer: !!form.config.washer_dryer,
        refrigerator: !!form.config.refrigerator,
        freezer: !!form.config.freezer,
        microwave: !!form.config.microwave,
        stovetop: !!form.config.stovetop,
        satellite: !!form.config.satellite,
        starlink: !!form.config.starlink,
        wifi: !!form.config.wifi,
        generator: !!form.config.generator,
        shore_power: !!form.config.shore_power,
        amenities: (form.config.amenities || []).filter((s) => s && s.trim()),
        custom_notes: form.config.custom_notes.trim() || null,
      },
      rate_defaults: Object.fromEntries(
        Object.entries(form.rate_defaults).map(([k, v]) => [k, toDecOrNull(v)])
      ),
    };
    // On create, don't send an empty fleet_number — let backend auto-generate.
    if (!isEdit && !p.fleet_number) delete p.fleet_number;
    // Drop empty date to null (already done above)
    return p;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.unit_name.trim()) {
      setTab("identity");
      return toast.error("Name / Unit Number is required");
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        try {
          await assetApi.patch(initial.id, payload);
        } catch (err) {
          // RC10j-10-14 · 409 driver_already_assigned → confirmation modal.
          const d = err?.response?.data?.detail;
          if (err?.response?.status === 409 && d?.code === "driver_already_assigned") {
            setSaving(false);
            setTransferConfirm({
              asset_id: initial.id,
              driver_id: d.driver_id,
              current_asset_id: d.current_asset_id,
              current_asset_label: d.current_asset_label,
              payload,
            });
            return;
          }
          throw err;
        }
      } else {
        await assetApi.create(payload);
      }
      toast.success(isEdit ? "Asset updated" : "Asset created");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmTransfer = async () => {
    if (!transferConfirm) return;
    setSaving(true);
    try {
      await assetApi.patch(
        transferConfirm.asset_id,
        transferConfirm.payload,
        { confirm_transfer: true },
      );
      toast.success("Asset updated");
      setTransferConfirm(null);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl w-full max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden bg-white"
        data-testid="asset-form-dialog"
      >
        <DialogHeader className="sticky top-0 z-10 bg-white border-b border-coach-silver px-6 py-3 shrink-0">
          <DialogTitle className="font-display">
            {isEdit ? "Edit Asset" : "New Asset"}
          </DialogTitle>
        </DialogHeader>

        <form
          id="af-form"
          onSubmit={submit}
          className="flex-1 min-h-0 flex flex-col px-6 pt-4 pb-0 text-[13px]"
        >
          <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col mt-3">
            <TabsList
              data-testid="af-tabs"
              className="bg-white border border-coach-silver rounded-sm w-full flex flex-wrap justify-start shrink-0 h-auto p-0.5 gap-0.5"
            >
              {[
                ["identity", "Identity"],
                ["operations", "Ops"],
                ...(isTypeTrailer ? [] : [["capacity", "Capacity"]]),
                ["rates", "Rates"],
                ["descriptions", "Notes"],
                ["advanced", "Advanced"],
              ].map(([k, l]) => (
                <TabsTrigger
                  key={k}
                  value={k}
                  data-testid={`af-tab-${k}`}
                  className="flex-1 min-w-[80px] data-[state=active]:bg-coach-blue data-[state=active]:text-white text-[12px]"
                >
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 min-h-0 overflow-y-auto pt-4 pr-1 pb-6">
              {/* ── Identity ─────────────────────────────────────────── */}
              <TabsContent value="identity" className="mt-0 space-y-3">
                <Field
                  label="Name / Unit Number"
                  required
                  hint='Any name or number the yard uses to identify this asset — e.g. "Blackbird", "118", "T12", "Coach 214".'
                >
                  <input
                    data-testid="af-unit-name"
                    required
                    autoFocus={!isEdit}
                    value={form.unit_name}
                    onChange={(e) => setField("unit_name", e.target.value)}
                    placeholder="e.g. Blackbird or 118"
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                    style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                  />
                </Field>

                <Field label="Type" required>
                  <select
                    data-testid="af-asset-type"
                    value={form.asset_type}
                    onChange={(e) => setField("asset_type", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                    style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                  >
                    {/* RC10f · Dynamic unit types from /api/unit-types.
                        Active types are offered. If the asset currently
                        carries an archived (historical) type, we render
                        it as `[Archived] <label>` and keep it selectable
                        for display, but it is disabled so the user
                        cannot re-select it on a new asset. */}
                    {unitTypes.filter(t => !t.is_archived).map((t) => (
                      <option key={t.code} value={t.code}>{t.label}</option>
                    ))}
                    {(() => {
                      const current = unitTypes.find(t => t.code === form.asset_type);
                      if (!current) {
                        // Dangling code — show raw value so operator can see it
                        if (form.asset_type) {
                          return (
                            <option key={form.asset_type} value={form.asset_type} disabled>
                              [Missing] {form.asset_type}
                            </option>
                          );
                        }
                        return null;
                      }
                      if (current.is_archived) {
                        return (
                          <option key={current.code} value={current.code} disabled>
                            [Archived] {current.label}
                          </option>
                        );
                      }
                      return null;
                    })()}
                  </select>
                </Field>

                <div className="grid grid-cols-3 gap-2">
                  <Field label="Year">
                    <input
                      data-testid="af-year"
                      value={form.year}
                      onChange={(e) => setField("year", e.target.value.replace(/[^\d]/g, ""))}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                  <Field label="Make">
                    <input
                      data-testid="af-make"
                      value={form.make}
                      onChange={(e) => setField("make", e.target.value)}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                  <Field label="Model">
                    <input
                      data-testid="af-model"
                      value={form.model}
                      onChange={(e) => setField("model", e.target.value)}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                </div>

                {!isTypeTrailer && (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Chassis">
                      <input
                        data-testid="af-chassis"
                        value={form.chassis}
                        onChange={(e) => setField("chassis", e.target.value)}
                        className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                        style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                      />
                    </Field>
                    <Field label="Converter">
                      <input
                        data-testid="af-converter"
                        value={form.converter}
                        onChange={(e) => setField("converter", e.target.value)}
                        className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                        style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                      />
                    </Field>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Field label="VIN">
                    <input
                      data-testid="af-vin"
                      value={form.vin}
                      onChange={(e) => setField("vin", e.target.value.toUpperCase())}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 font-mono text-[12px]"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                  <Field label="License Plate">
                    <input
                      data-testid="af-plate"
                      value={form.license_plate}
                      onChange={(e) => setField("license_plate", e.target.value)}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Field label="Registration State">
                    <select
                      data-testid="af-reg-state"
                      value={form.registration_state}
                      onChange={(e) => setField("registration_state", e.target.value)}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    >
                      <option value="">— None —</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Registration Expiration">
                    <input
                      type="date"
                      data-testid="af-reg-exp"
                      value={form.registration_expiration || ""}
                      onChange={(e) => setField("registration_expiration", e.target.value)}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Field label="Engine Make / Model">
                    <input
                      data-testid="af-engine-make-model"
                      value={form.engine_make_model}
                      onChange={(e) => setField("engine_make_model", e.target.value)}
                      placeholder="e.g. Detroit DD15"
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                  <Field label="Engine Serial Number">
                    <input
                      data-testid="af-engine-serial"
                      value={form.engine_serial}
                      onChange={(e) => setField("engine_serial", e.target.value.toUpperCase())}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 font-mono text-[12px]"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Field label="Door Code" hint="Internal only — never shown to clients or on Quotes.">
                    <input
                      data-testid="af-door-code"
                      value={form.door_code}
                      onChange={(e) => setField("door_code", e.target.value)}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 font-mono text-[12px]"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                </div>
              </TabsContent>

              {/* ── Operations ──────────────────────────────────────── */}
              <TabsContent value="operations" className="mt-0 space-y-3">
                <Field label="Asset Owner Organization">
                  {/* RC10f · Correction C — searchable owner org picker.
                      Shows current owner (with [Archived] prefix if the
                      stored org is archived, [Missing] if dangling).
                      Only active orgs are offered for assignment. */}
                  <div className="relative">
                    <div
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 flex items-center justify-between gap-2"
                      data-testid="af-owner-org"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    >
                      <span className="truncate text-sm" data-testid="af-owner-org-current">
                        {!form.owner_organization_id && (
                          <span className="text-steel">— No owner organization / Company-owned —</span>
                        )}
                        {form.owner_organization_id && currentOwnerMissing && (
                          <span className="text-maintenance-orange">Missing organization</span>
                        )}
                        {form.owner_organization_id && !currentOwnerMissing && currentOwnerArchived && (
                          <span className="text-maintenance-orange">[Archived] {currentOwnerName}</span>
                        )}
                        {form.owner_organization_id && !currentOwnerMissing && !currentOwnerArchived && (
                          <span>{currentOwnerName}</span>
                        )}
                      </span>
                      <button type="button"
                        data-testid="af-owner-org-change"
                        onClick={() => setOwnerPanelOpen((v) => !v)}
                        className="text-coach-blue text-xs underline">
                        {ownerPanelOpen ? "Close" : "Change"}
                      </button>
                    </div>
                    {ownerPanelOpen && (
                      <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-coach-silver rounded-sm shadow-md max-h-72 overflow-y-auto"
                        data-testid="af-owner-org-panel">
                        <input
                          autoFocus
                          data-testid="af-owner-org-search"
                          value={ownerQuery}
                          onChange={(e) => setOwnerQuery(e.target.value)}
                          placeholder="Search organizations…"
                          className="w-full border-b border-coach-silver px-2.5 py-1.5 text-sm"
                        />
                        <button type="button"
                          data-testid="af-owner-org-option-none"
                          onClick={() => { setField("owner_organization_id", ""); setOwnerPanelOpen(false); setOwnerQuery(""); }}
                          className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-road-white border-b border-coach-silver/50 text-steel">
                          — No owner organization / Company-owned —
                        </button>
                        {ownerLoading && <div className="px-2.5 py-2 text-xs text-steel">Loading…</div>}
                        {!ownerLoading && ownerOptions.length === 0 && (
                          <div className="px-2.5 py-2 text-xs text-steel">No matches</div>
                        )}
                        {!ownerLoading && ownerOptions.map((o) => (
                          <button type="button"
                            key={o.id}
                            data-testid={`af-owner-org-option-${o.id}`}
                            onClick={() => { setField("owner_organization_id", o.id); setOwnerPanelOpen(false); setOwnerQuery(""); }}
                            className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-road-white">
                            <div className="truncate">{o.display_name}</div>
                            <div className="text-[11px] text-steel">{o.org_type}</div>
                          </button>
                        ))}
                        <div className="border-t border-coach-silver/50 bg-road-white flex items-center justify-between px-2.5 py-1.5 text-xs">
                          <a
                            data-testid="af-owner-org-manage"
                            href="/fleet/asset-owners"
                            target="_blank"
                            rel="noreferrer"
                            className="text-coach-blue underline"
                          >
                            Manage Asset Owners
                          </a>
                          <button
                            type="button"
                            data-testid="af-owner-org-quickadd"
                            className="text-coach-blue underline"
                            onClick={() => setQuickAddOpen(true)}
                          >
                            + Add Asset Owner
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* RC10h · Compact read-only owner summary. Hidden when there
                      is no owner or the stored owner isn't a Asset Owner. */}
                  {form.owner_organization_id && ownerSummary && (
                    <div
                      data-testid="af-owner-summary"
                      className="mt-2 border border-coach-silver/50 rounded-sm bg-road-white px-3 py-2 text-xs text-steel"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <a
                          data-testid="af-owner-summary-company"
                          href={`/fleet/asset-owners`}
                          className="text-coach-blue underline font-medium"
                        >
                          {ownerSummary.display_name}
                        </a>
                        {ownerSummary.billing_address?.city &&
                          ownerSummary.billing_address?.state && (
                            <span>
                              · {ownerSummary.billing_address.city},{" "}
                              {ownerSummary.billing_address.state}
                            </span>
                          )}
                      </div>
                      {ownerSummary.owner_people?.length > 0 && (
                        <div
                          data-testid="af-owner-summary-people"
                          className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
                        >
                          {ownerSummary.owner_people.map((p) => (
                            <span key={p.relationship_id}>
                              <a
                                className="text-coach-blue underline"
                                href={`/contacts/${p.contact_id}`}
                              >
                                {p.full_name}
                              </a>
                              {p.primary_phone && (
                                <span className="ml-1">
                                  · {p.primary_phone}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {quickAddOpen && (
                    <QuickAddOwner
                      onClose={() => setQuickAddOpen(false)}
                      onCreated={(created) => {
                        setQuickAddOpen(false);
                        setField("owner_organization_id", created.id);
                        setOwnerPanelOpen(false);
                        setOwnerQuery("");
                        // Refetch dropdown so the new row appears without a page reload.
                        assetOwnerApi.list({ page_size: 25 })
                          .then((d) => setOwnerOptions(d?.results || []));
                      }}
                    />
                  )}
                </Field>

                <Field label="Ownership Type">
                  <select
                    data-testid="af-ownership-type"
                    value={form.ownership_type || ""}
                    onChange={(e) => setField("ownership_type", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                    style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                  >
                    <option value="">— None —</option>
                    {OWNERSHIP_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <label className="flex items-center gap-2 text-[12.5px]" style={{ color: "var(--fg-primary)" }}>
                  <input
                    type="checkbox"
                    data-testid="af-active"
                    checked={form.active}
                    onChange={(e) => setField("active", e.target.checked)}
                  />
                  Active
                </label>

                {/* RC10j-10-14 · Primary Assigned Driver — home/normal unit assignment.
                    Separate from Tour/Schedule/Dispatch driver fields. */}
                <Field label="Primary Assigned Driver" hint="The driver's home unit. Independent of tour / schedule / dispatch assignments.">
                  <div className="relative">
                    <div
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 flex items-center justify-between gap-2"
                      data-testid="af-primary-driver"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    >
                      <span className="truncate text-sm" data-testid="af-primary-driver-current">
                        {!form.primary_driver_id && (
                          <span className="text-steel">— No primary driver —</span>
                        )}
                        {form.primary_driver_id && currentDriverMissing && (
                          <span className="text-maintenance-orange">[Missing Driver]</span>
                        )}
                        {form.primary_driver_id && !currentDriverMissing && currentDriverArchived && (
                          <span className="text-maintenance-orange">[Archived] {currentDriverLabel}</span>
                        )}
                        {form.primary_driver_id && !currentDriverMissing && !currentDriverArchived && (
                          <span>{currentDriverLabel}</span>
                        )}
                      </span>
                      <button type="button"
                        data-testid="af-primary-driver-change"
                        onClick={() => setDriverPanelOpen((v) => !v)}
                        className="text-coach-blue text-xs underline">
                        {driverPanelOpen ? "Close" : "Change"}
                      </button>
                    </div>
                    {driverPanelOpen && (
                      <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-coach-silver rounded-sm shadow-md max-h-72 overflow-y-auto"
                        data-testid="af-primary-driver-panel">
                        <input
                          autoFocus
                          data-testid="af-primary-driver-search"
                          value={driverQuery}
                          onChange={(e) => setDriverQuery(e.target.value)}
                          placeholder="Search drivers…"
                          className="w-full border-b border-coach-silver px-2.5 py-1.5 text-sm"
                        />
                        <button type="button"
                          data-testid="af-primary-driver-option-none"
                          onClick={() => { setField("primary_driver_id", ""); setDriverPanelOpen(false); setDriverQuery(""); }}
                          className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-road-white border-b border-coach-silver/50 text-steel">
                          — No primary driver —
                        </button>
                        {driverLoading && <div className="px-2.5 py-2 text-xs text-steel">Loading…</div>}
                        {!driverLoading && driverOptions.length === 0 && (
                          <div className="px-2.5 py-2 text-xs text-steel">No matches</div>
                        )}
                        {!driverLoading && driverOptions.map((d) => (
                          <button type="button"
                            key={d.id}
                            data-testid={`af-primary-driver-option-${d.id}`}
                            onClick={() => { setField("primary_driver_id", d.id); setDriverPanelOpen(false); setDriverQuery(""); }}
                            className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-road-white">
                            <div className="truncate">{d.display_name}</div>
                            <div className="text-[11px] text-steel">{d.driver_classification || "driver"}</div>
                          </button>
                        ))}
                        <div className="border-t border-coach-silver/50 bg-road-white flex items-center justify-between px-2.5 py-1.5 text-xs">
                          <a
                            data-testid="af-primary-driver-manage"
                            href="/drivers"
                            target="_blank"
                            rel="noreferrer"
                            className="text-coach-blue underline"
                          >
                            Manage Drivers
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </Field>

                {isEdit && (
                  <div className="text-[11.5px] text-steel border-l-2 border-coach-silver pl-3">
                    Operational status is changed via the “Change status” button on the
                    Coach Detail page — not from this form.
                  </div>
                )}
              </TabsContent>

              {/* ── Capacity & Features (coaches only) ───────────────── */}
              {!isTypeTrailer && (
                <TabsContent value="capacity" className="mt-0 space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    <IntField label="Bunks" value={form.config.bunk_count} onChange={(v) => setConfig("bunk_count", v)} testid="af-cfg-bunk-count" />
                    <IntField label="Slides" value={form.config.slide_count} onChange={(v) => setConfig("slide_count", v)} testid="af-cfg-slide-count" />
                    <IntField label="TVs" value={form.config.tv_count} onChange={(v) => setConfig("tv_count", v)} testid="af-cfg-tv-count" />
                  </div>

                  <div>
                    <SectionLabel>Water Tanks (gallons)</SectionLabel>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <IntField label="Fresh" value={form.config.fresh_water_gal} onChange={(v) => setConfig("fresh_water_gal", v)} testid="af-cfg-fresh-water" />
                      <IntField label="Gray" value={form.config.gray_water_gal} onChange={(v) => setConfig("gray_water_gal", v)} testid="af-cfg-gray-water" />
                      <IntField label="Black" value={form.config.black_water_gal} onChange={(v) => setConfig("black_water_gal", v)} testid="af-cfg-black-water" />
                    </div>
                  </div>

                  <div>
                    <SectionLabel>Layout</SectionLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 mt-1">
                      <BoolField label="Shower" checked={form.config.shower} onChange={(v) => setConfig("shower", v)} testid="af-cfg-shower" />
                      <BoolField label="Washer / Dryer" checked={form.config.washer_dryer} onChange={(v) => setConfig("washer_dryer", v)} testid="af-cfg-washer-dryer" />
                    </div>
                  </div>

                  <div>
                    <SectionLabel>Galley</SectionLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 mt-1">
                      <BoolField label="Refrigerator" checked={form.config.refrigerator} onChange={(v) => setConfig("refrigerator", v)} testid="af-cfg-refrigerator" />
                      <BoolField label="Freezer" checked={form.config.freezer} onChange={(v) => setConfig("freezer", v)} testid="af-cfg-freezer" />
                      <BoolField label="Microwave" checked={form.config.microwave} onChange={(v) => setConfig("microwave", v)} testid="af-cfg-microwave" />
                      <BoolField label="Stovetop" checked={form.config.stovetop} onChange={(v) => setConfig("stovetop", v)} testid="af-cfg-stovetop" />
                    </div>
                  </div>

                  <div>
                    <SectionLabel>Connectivity</SectionLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 mt-1">
                      <BoolField label="Starlink" checked={form.config.starlink} onChange={(v) => setConfig("starlink", v)} testid="af-cfg-starlink" />
                    </div>
                  </div>

                  <Field
                    label="Additional Amenities"
                    hint="Comma-separated list. E.g. “ice maker, roof-top A/C, king bed”."
                  >
                    <input
                      data-testid="af-cfg-amenities"
                      value={(form.config.amenities || []).join(", ")}
                      onChange={(e) =>
                        setConfig(
                          "amenities",
                          e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                        )
                      }
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>

                  <Field label="Configuration Notes">
                    <textarea
                      data-testid="af-cfg-notes"
                      rows={3}
                      value={form.config.custom_notes}
                      onChange={(e) => setConfig("custom_notes", e.target.value)}
                      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                    />
                  </Field>
                </TabsContent>
              )}

              {/* ── Rates & Pricing ─────────────────────────────────── */}
              <TabsContent value="rates" className="mt-0 space-y-3">
                <div className="text-[11.5px] text-steel">
                  Money in USD. Percent fields are whole numbers (e.g. 15 = 15%).
                  Historical Quotes and Contracts snapshot rates at send / execute
                  time and are not affected by future rate edits.
                </div>
                <div>
                  <SectionLabel>Standard Rates</SectionLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                    <MoneyField label="Daily" value={form.rate_defaults.daily_rate} onChange={(v) => setRate("daily_rate", v)} testid="af-rate-daily" />
                    <MoneyField label="Monthly" value={form.rate_defaults.monthly_rate} onChange={(v) => setRate("monthly_rate", v)} testid="af-rate-monthly" />
                    <MoneyField label="Mileage (/mi)" value={form.rate_defaults.mileage_rate} onChange={(v) => setRate("mileage_rate", v)} testid="af-rate-mileage" />
                  </div>
                </div>

                <div>
                  <SectionLabel>Commission</SectionLabel>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <MoneyField label="Commission %" percent value={form.rate_defaults.commission_percent} onChange={(v) => setRate("commission_percent", v)} testid="af-rate-comm-percent" />
                    <MoneyField label="Commission Flat" value={form.rate_defaults.commission_flat} onChange={(v) => setRate("commission_flat", v)} testid="af-rate-comm-flat" />
                  </div>
                </div>
              </TabsContent>

              {/* ── Descriptions & Notes ────────────────────────────── */}
              <TabsContent value="descriptions" className="mt-0 space-y-3">
                <Field
                  label="Quote Description"
                  hint="Customer-visible description shown on Quotes. Kept separate from internal notes."
                >
                  <textarea
                    data-testid="af-quote-description"
                    rows={3}
                    value={form.quote_description}
                    onChange={(e) => setField("quote_description", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                    style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                  />
                </Field>
                <Field
                  label="Schedule Description"
                  hint="Short label shown on the internal Schedule / Dispatch views."
                >
                  <textarea
                    data-testid="af-schedule-description"
                    rows={2}
                    value={form.schedule_description}
                    onChange={(e) => setField("schedule_description", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                    style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                  />
                </Field>
                <Field label="Internal Notes">
                  <textarea
                    data-testid="af-notes"
                    rows={4}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                    style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
                  />
                </Field>
              </TabsContent>

              {/* ── Advanced (photo) ────────────────────────────────── */}
              <TabsContent value="advanced" className="mt-0 space-y-3">
                <div data-testid="af-photo-section">
                  <SectionLabel>Primary Photo</SectionLabel>
                  <div className="mt-1">
                    {photoPreviewUrl ? (
                      <div className="relative inline-block">
                        <img
                          data-testid="af-photo-preview"
                          src={photoPreviewUrl}
                          alt="Asset"
                          className="rounded-sm border border-coach-silver max-h-40 max-w-full object-cover"
                        />
                        <button
                          type="button"
                          data-testid="af-photo-remove"
                          onClick={() => setField("primary_photo_file_id", null)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full grid place-items-center text-white bg-critical-red/90 hover:bg-critical-red"
                          aria-label="Remove photo"
                          title="Remove photo"
                        >
                          <XIcon size={14} />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="af-photo-input"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-sm text-[12.5px] font-medium text-coach-blue border border-coach-silver hover:bg-chrome-mist cursor-pointer"
                      >
                        <ImagePlus size={14} />
                        {uploadingPhoto ? "Uploading…" : "Upload photo"}
                      </label>
                    )}
                    <input
                      id="af-photo-input"
                      data-testid="af-photo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onPhotoPicked(e.target.files?.[0])}
                    />
                    <div className="text-[10.5px] text-steel mt-1">
                      Optional. Images are stored privately and rendered with a short-lived signed token.
                    </div>
                  </div>
                </div>
                {isEdit && (
                  <div className="text-[11.5px] text-steel border-l-2 border-coach-silver pl-3">
                    Additional documents (registration, title, insurance, etc.) are
                    uploaded from the <strong>Documents</strong> tab on the Coach detail page.
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </form>

        {/* RC10h · Sticky bottom action bar — Archive left, Close + Save Changes right.
            Lives OUTSIDE the <form> so it never gets re-laid-out by Tabs; Save
            still submits the form via the `form` attribute. */}
        <DialogFooter className="sticky bottom-0 z-10 bg-white border-t border-coach-silver px-6 py-3 shrink-0 flex items-center justify-between sm:justify-between gap-2 flex-wrap">
          <div className="flex items-center">
            {isEdit && (
              <button
                type="button"
                data-testid="af-archive"
                onClick={handleArchive}
                disabled={archiving || saving}
                className="px-3 py-1.5 text-[12.5px] text-critical-red border border-critical-red rounded-sm hover:bg-critical-red hover:text-white disabled:opacity-60"
              >
                {archiving ? "Archiving…" : "Archive"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="af-close"
              onClick={onClose}
              className="px-3 py-1.5 text-[12.5px] text-steel border border-coach-silver rounded-sm hover:bg-chrome-mist"
            >
              Close
            </button>
            <button
              type="submit"
              form="af-form"
              data-testid="af-submit"
              disabled={saving}
              className="px-4 py-1.5 text-[12.5px] font-medium bg-coach-blue hover:bg-deep-road-navy text-white rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Asset"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
      {/* RC10j-10-14 · Transfer confirmation on 409 from the server. */}
      {transferConfirm && (
        <Dialog open onOpenChange={(o) => !o && setTransferConfirm(null)}>
          <DialogContent
            className="max-w-md w-full bg-white"
            data-testid="af-transfer-confirm"
          >
            <DialogHeader>
              <DialogTitle className="font-display">Transfer Primary Driver?</DialogTitle>
            </DialogHeader>
            <div className="text-[13px] text-carbon py-2" data-testid="af-transfer-confirm-copy">
              This driver is currently assigned to{" "}
              <strong>{transferConfirm.current_asset_label || "another unit"}</strong>.
              Do you want to move the driver to this unit?
            </div>
            <DialogFooter className="pt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                data-testid="af-transfer-cancel"
                className="px-3 py-1.5 text-[12.5px] font-medium border border-coach-silver rounded-sm"
                onClick={() => setTransferConfirm(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="af-transfer-confirm-btn"
                className="px-3 py-1.5 text-[12.5px] font-medium bg-coach-blue text-white rounded-sm disabled:opacity-60"
                onClick={confirmTransfer}
                disabled={saving}
              >
                {saving ? "Transferring…" : "Confirm"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

/* ─── small building blocks ─────────────────────────────────────── */

const Field = ({ label, required, children, hint }) => (
  <label className="block">
    <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
      {label} {required && <span className="text-critical-red">*</span>}
    </div>
    {children}
    {hint && <div className="text-[11px] text-steel mt-1">{hint}</div>}
  </label>
);

const SectionLabel = ({ children }) => (
  <div className="text-[10.5px] font-semibold uppercase tracking-widest text-carbon">
    {children}
  </div>
);

const IntField = ({ label, value, onChange, testid }) => (
  <Field label={label}>
    <input
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))}
      inputMode="numeric"
      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 font-mono text-[12px]"
      style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
    />
  </Field>
);

const MoneyField = ({ label, value, onChange, testid, percent = false }) => (
  <Field label={label}>
    <div className="relative">
      <span
        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-steel text-[12px]"
        aria-hidden="true"
      >
        {percent ? "%" : "$"}
      </span>
      <input
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ""))}
        inputMode="decimal"
        placeholder="0.00"
        className="w-full border border-coach-silver rounded-sm pl-6 pr-2.5 py-1.5 font-mono text-[12px]"
        style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
      />
    </div>
  </Field>
);

const BoolField = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-[12.5px]" style={{ color: "var(--fg-primary)" }}>
    <input
      type="checkbox"
      data-testid={testid}
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    {label}
  </label>
);


// RC10h · In-place "quick-add" dialog for asset owners. Opens from
// inside the Asset Owner Organization picker so operators can create
// a new coach_owner / trailer_owner org without leaving the Asset Edit
// flow. Reuses the SAME shared design tokens as the Edit Asset dialog
// (bg-white panel, coach-silver borders, coach-blue primary, graphite
// labels, --fg-primary input text).
function QuickAddOwner({ onClose, onCreated }) {
  const [form, setForm] = React.useState({
    display_name: "",
    org_type: "coach_owner",
  });
  const [saving, setSaving] = React.useState(false);
  const save = async () => {
    if (!form.display_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      const created = await assetOwnerApi.create(form);
      toast.success("Asset owner created");
      onCreated(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Failed to create");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm bg-white"
        data-testid="af-owner-quickadd-dialog"
      >
        <DialogHeader className="border-b border-coach-silver pb-3">
          <DialogTitle className="text-graphite">Add Asset Owner</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <label className="block text-sm">
            <span className="text-graphite font-medium">Company name</span>
            <input
              data-testid="af-owner-quickadd-name"
              className="mt-1 w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
              style={{ color: "var(--fg-primary)" }}
              value={form.display_name}
              onChange={(e) =>
                setForm({ ...form, display_name: e.target.value })
              }
            />
          </label>
          <label className="block text-sm">
            <span className="text-graphite font-medium">Type</span>
            <select
              data-testid="af-owner-quickadd-type"
              className="mt-1 w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
              style={{ color: "var(--fg-primary)" }}
              value={form.org_type}
              onChange={(e) => setForm({ ...form, org_type: e.target.value })}
            >
              <option value="coach_owner">Coach owner</option>
              <option value="trailer_owner">Trailer owner</option>
            </select>
          </label>
        </div>
        <DialogFooter className="border-t border-coach-silver pt-3">
          <button
            className="inline-flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm border border-coach-silver text-graphite hover:bg-chrome-mist"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            data-testid="af-owner-quickadd-save"
            className="inline-flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm bg-coach-blue text-white hover:bg-deep-road-navy"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving…" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

